import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, TwoCol, EditorModal } from "@/components/AdminPostsPanel";

const EMPTY_CASE = {
  slug: "",
  title: "",
  category: "Software",
  tagline: "",
  metric: "",
  summary: "",
  external_url: "",
  cover_img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1600&q=75",
  color: "#00F0FF",
  year: "2025",
  role: "",
  challenge: "",
  approach: "",
  outcome: "",
  services: [],
  results: [],
  gallery: [],
  featured: false,
  order: 100,
};

const inputCls = "bg-black/40 border-white/10 text-white focus-visible:ring-[#00F0FF] focus-visible:border-[#00F0FF]";

export default function AdminCasesPanel() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cases");
      setCases(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => setEditing({ mode: "create", original: null, data: { ...EMPTY_CASE } });
  const openEdit = (c) => setEditing({
    mode: "edit",
    original: c.slug,
    data: {
      ...EMPTY_CASE,
      ...c,
      services: c.services || [],
      results: c.results || [],
      gallery: c.gallery || [],
      external_url: c.external_url || "",
    },
  });

  const updateField = (k, v) => setEditing((e) => ({ ...e, data: { ...e.data, [k]: v } }));

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    const payload = { ...editing.data };
    // Convert comma-separated strings into arrays if user typed them as strings
    if (typeof payload.services === "string") {
      payload.services = payload.services.split(",").map((s) => s.trim()).filter(Boolean);
    }
    if (typeof payload.gallery === "string") {
      payload.gallery = payload.gallery.split("\n").map((s) => s.trim()).filter(Boolean);
    }
    if (typeof payload.results === "string") {
      payload.results = payload.results
        .split("\n")
        .map((ln) => ln.split("|"))
        .filter((arr) => arr.length === 2)
        .map(([k, v]) => ({ k: k.trim(), v: v.trim() }));
    }
    if (!payload.external_url) payload.external_url = null;
    try {
      if (editing.mode === "create") {
        await api.post("/admin/cases", payload);
        toast.success("Case created");
      } else {
        await api.put(`/admin/cases/${editing.original}`, payload);
        toast.success("Case updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete case '${slug}'? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/cases/${slug}`);
      toast.success("Case deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-cases-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-xl font-bold">Case studies</h3>
          <p className="text-xs text-white/50 font-mono-neo uppercase tracking-[0.2em]">{cases.length} total</p>
        </div>
        <button onClick={openCreate} data-testid="admin-new-case" className="btn-primary !px-5 !py-2.5 !text-sm">
          <Plus className="w-4 h-4" /> New case
        </button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#0A0A0C] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : cases.length === 0 ? (
          <div className="p-10 text-center text-white/40 text-sm">No cases yet</div>
        ) : (
          <div className="divide-y divide-white/5">
            {cases.map((c) => (
              <div key={c.slug} data-testid={`admin-case-row-${c.slug}`} className="p-5 flex flex-wrap items-center gap-4 hover:bg-white/5 transition-colors">
                <img src={c.cover_img} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1 min-w-[200px]">
                  <div className="font-display font-bold truncate">{c.title}</div>
                  <div className="text-xs text-white/50 truncate">/{c.slug} · {c.year}</div>
                </div>
                <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em]" style={{ color: c.color || "#00F0FF" }}>{c.category}</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openEdit(c)} className="p-2 rounded-lg border border-white/10 hover:border-[#00F0FF]/50 hover:text-[#00F0FF]" title="Edit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(c.slug)} data-testid={`admin-case-delete-${c.slug}`} className="p-2 rounded-lg border border-white/10 hover:border-red-500/50 hover:text-red-400" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <EditorModal
          title={editing.mode === "create" ? "New case study" : `Edit: ${editing.original}`}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          saving={saving}
        >
          <TwoCol>
            <Field label="Slug" required><Input value={editing.data.slug} onChange={(e) => updateField("slug", e.target.value)} placeholder="my-case-slug" className={inputCls} /></Field>
            <Field label="Title" required><Input value={editing.data.title} onChange={(e) => updateField("title", e.target.value)} className={inputCls} /></Field>
          </TwoCol>
          <Field label="Tagline" hint="Short one-liner shown on card + hero" required>
            <Input value={editing.data.tagline} onChange={(e) => updateField("tagline", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Summary" required><Textarea value={editing.data.summary} onChange={(e) => updateField("summary", e.target.value)} className={`${inputCls} min-h-[80px]`} /></Field>
          <TwoCol>
            <Field label="Category" required><Input value={editing.data.category} onChange={(e) => updateField("category", e.target.value)} className={inputCls} /></Field>
            <Field label="Year" required><Input value={editing.data.year} onChange={(e) => updateField("year", e.target.value)} className={inputCls} /></Field>
          </TwoCol>
          <TwoCol>
            <Field label="Role" required><Input value={editing.data.role} onChange={(e) => updateField("role", e.target.value)} className={inputCls} /></Field>
            <Field label="Metric / Headline stat" required><Input value={editing.data.metric} onChange={(e) => updateField("metric", e.target.value)} className={inputCls} /></Field>
          </TwoCol>
          <TwoCol>
            <Field label="External URL" hint="Leave empty if none"><Input value={editing.data.external_url || ""} onChange={(e) => updateField("external_url", e.target.value)} className={inputCls} /></Field>
            <Field label="Accent color" hint="#00F0FF / #FF00FF / #39FF14"><Input value={editing.data.color} onChange={(e) => updateField("color", e.target.value)} className={inputCls} /></Field>
          </TwoCol>
          <Field label="Cover image URL" required><Input value={editing.data.cover_img} onChange={(e) => updateField("cover_img", e.target.value)} className={inputCls} /></Field>
          <Field label="Challenge" required><Textarea value={editing.data.challenge} onChange={(e) => updateField("challenge", e.target.value)} className={`${inputCls} min-h-[80px]`} /></Field>
          <Field label="Approach" required><Textarea value={editing.data.approach} onChange={(e) => updateField("approach", e.target.value)} className={`${inputCls} min-h-[80px]`} /></Field>
          <Field label="Outcome" required><Textarea value={editing.data.outcome} onChange={(e) => updateField("outcome", e.target.value)} className={`${inputCls} min-h-[80px]`} /></Field>
          <Field label="Services" hint="Comma-separated list">
            <Input
              value={Array.isArray(editing.data.services) ? editing.data.services.join(", ") : editing.data.services}
              onChange={(e) => updateField("services", e.target.value)}
              className={inputCls}
              placeholder="Software Development, SaaS Platform"
            />
          </Field>
          <Field label="Results" hint="One per line, format: Key | Value (e.g. '10k+ | Quotes/month')">
            <Textarea
              value={Array.isArray(editing.data.results) ? editing.data.results.map((r) => `${r.k} | ${r.v}`).join("\n") : editing.data.results}
              onChange={(e) => updateField("results", e.target.value)}
              className={`${inputCls} min-h-[100px] font-mono-neo text-sm`}
              placeholder={"10k+ | Quotes per month\n65% | Faster loads"}
            />
          </Field>
          <Field label="Gallery URLs" hint="One URL per line (cover is auto-included)">
            <Textarea
              value={Array.isArray(editing.data.gallery) ? editing.data.gallery.join("\n") : editing.data.gallery}
              onChange={(e) => updateField("gallery", e.target.value)}
              className={`${inputCls} min-h-[80px] font-mono-neo text-sm`}
            />
          </Field>
          <TwoCol>
            <Field label="Order" hint="Lower = earlier"><Input type="number" value={editing.data.order} onChange={(e) => updateField("order", parseInt(e.target.value) || 100)} className={inputCls} /></Field>
            <label className="flex items-center gap-2 text-sm text-white/70 mt-6">
              <input type="checkbox" checked={editing.data.featured} onChange={(e) => updateField("featured", e.target.checked)} className="accent-[#00F0FF]" />
              Featured on Work page
            </label>
          </TwoCol>
        </EditorModal>
      )}
    </div>
  );
}
